// CLAUDE-TODO #383: pure helpers for deciding whether a Put.io file/folder is a
// "confident" library match for a wanted title — extracted from
// BrowseMediaView.vue's loadPutioMatch() so the Monitoring view can reuse the
// SAME scoring without dragging in the episode-walk path.
//
// Pure ESM, no Vue/@ imports → node --test'able. The network call
// (putioService.searchFiles) lives in src/services/putio.js — this module only
// builds candidates and scores filenames.

// Strip combining-mark diacritics so "Gérald le Conquérant" and
// "Gerald Le Conquerant" compare as equal. Put.io filenames lose accents during
// transcoding/release-group rename, but TMDB keeps the original-language form.
export function stripDiacritics(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

// Reduce a title to a punctuation- and space-insensitive alphanumeric "slug"
// for loose comparison. The auto-organizer's filenameSanitizer rewrites the
// characters TMDB keeps in a title — `:` (e.g. "UFOs: Investigating the
// Unknown" is stored as "UFOs Investigating the Unknown"), `?`, `/`, `"`, `*`,
// etc. — so a raw substring compare against the TMDB title silently misses the
// downloaded copy. Folding BOTH sides to `[a-z0-9]` only makes the comparison
// immune to whatever the sanitizer did with the punctuation (drop it, replace
// it with a space, or swap it for "-"), since none of that survives the fold.
export function normalizeTitleForMatch(s) {
  return stripDiacritics(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

// Build the de-duped list of plain-text title candidates to search Put.io for.
// Accepts the localized title plus the original-language title(s) so a show
// stored under its native name still matches. Candidates are diacritic-stripped
// and whitespace-collapsed; anything under 2 chars is dropped.
export function buildPutioTitleCandidates({
  title,
  originalTitle,
  originalName,
} = {}) {
  const out = [];
  const seen = new Set();
  for (const raw of [title, originalTitle, originalName]) {
    const candidate = stripDiacritics(raw).replace(/\s+/g, " ").trim();
    if (candidate.length < 2) continue;
    const key = candidate.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(candidate);
  }
  return out;
}

// Lightweight title check: the parsed filename title contains OR is contained by
// one of the wanted titles ("The White Lotus" vs "White Lotus"). Both sides are
// expected to be normalized to an alphanumeric slug by the caller (see
// normalizeTitleForMatch) so punctuation/spacing the sanitizer rewrote can't
// break the compare.
function titleHits(parsedSlug, wantedSlugs) {
  return wantedSlugs.some(
    (wanted) => parsedSlug.includes(wanted) || wanted.includes(parsedSlug),
  );
}

// Pick EVERY confident Put.io match from a list of files (a library can hold
// several distinct copies of one title — e.g. a 1080p remux AND a 2160p remux
// of the same movie; the /browse source picker lists them all).
//
// A file/folder is a confident match when:
//   - it's a VIDEO or FOLDER (never a subtitle/image/etc.), AND
//   - its cleaned filename title strongly matches a wanted title, AND
//   - if BOTH the wanted record and the parsed filename carry a year, the years
//     are equal (drops "Wicked" 1998 when we want the 2024 remake).
//
// A matched child of a matched FOLDER is dropped (search returns both the
// movie's folder and the video inside it — that's ONE copy). FOLDERs sort
// first (stable), so [0] is identical to the historical single-best pick.
//
// `cleanTitle(name)` is injected (the pure clean-media-title helper) so this
// stays Vue-free and unit-testable. Returns [{ id, name, size, file_type }].
export function pickConfidentPutioMatches({
  files,
  wantedTitles,
  wantedYear,
  cleanTitle,
} = {}) {
  if (!Array.isArray(files) || files.length === 0) return [];
  const wantedSlugs = (Array.isArray(wantedTitles) ? wantedTitles : [])
    .map((t) => normalizeTitleForMatch(t))
    .filter((t) => t.length > 0);
  if (wantedSlugs.length === 0) return [];
  const wantYear = wantedYear ? String(wantedYear).slice(0, 4) : "";

  const hits = [];
  for (const f of files) {
    if (!f || (f.file_type !== "VIDEO" && f.file_type !== "FOLDER")) continue;
    const parsed = typeof cleanTitle === "function" ? cleanTitle(f.name) : null;
    const parsedSlug = normalizeTitleForMatch(parsed?.title || f.name);
    if (!titleHits(parsedSlug, wantedSlugs)) continue;
    if (wantYear && parsed?.year && String(parsed.year) !== wantYear) continue;
    hits.push(f);
  }
  if (hits.length === 0) return [];

  const folderIds = new Set(
    hits.filter((f) => f.file_type === "FOLDER").map((f) => f.id),
  );
  const deduped = hits.filter(
    (f) => !(f.parent_id != null && folderIds.has(f.parent_id)),
  );
  const ordered = [
    ...deduped.filter((f) => f.file_type === "FOLDER"),
    ...deduped.filter((f) => f.file_type !== "FOLDER"),
  ];
  return ordered.map((f) => ({
    id: f.id,
    name: f.name,
    size: f.size ?? null,
    file_type: f.file_type,
  }));
}

// The single most-confident match (first FOLDER, else first hit) — kept for
// callers that only need one copy (WatchlistView's "On Put.io ✓" pill).
// Returns { id, name } or null.
export function pickConfidentPutioMatch(options = {}) {
  const [best] = pickConfidentPutioMatches(options);
  return best ? { id: best.id, name: best.name } : null;
}

// Flatten the per-candidate searchFiles() responses into a de-duped file list.
// Each response is Put.io's `{ files: [...] }` shape. Pure — accepts already
// resolved/settled values.
export function dedupePutioSearchFiles(searchResponses) {
  const filesById = new Map();
  for (const resp of Array.isArray(searchResponses) ? searchResponses : []) {
    const batch = Array.isArray(resp?.files) ? resp.files : [];
    for (const file of batch) {
      if (file?.id != null && !filesById.has(file.id)) {
        filesById.set(file.id, file);
      }
    }
  }
  return Array.from(filesById.values());
}
